// agentStream.ts -- SSE streaming pipeline for /chat message sends.
//
// Extracted from the legacy useAgent.tsx so both the singleton hook
// (during the I16 transition) and the new windows-registry chatStore
// can share the same wire-protocol logic. No state ownership lives
// here; callers pass an updater for the messages array and a side-
// effect dispatcher.
//
// Wire shape (matches server/src/routes/chat.ts):
//   event: user-saved      -- BFF wrote the user message droplet
//   event: thinking        -- agent loop iteration started
//   event: tool-call       -- tool invocation begun
//   event: tool-result     -- tool returned (ok=true/false)
//   event: tool-error      -- tool threw
//   event: side-effect     -- intent for the UI (refresh, toast, etc)
//   event: session-title   -- auto-titler produced a title
//   event: final           -- agent loop's last reply
//   event: assistant-saved -- BFF wrote the assistant droplet
//   event: error           -- terminal failure
//   event: done            -- stream end (also sent for human-only mode)
import { getActiveToken } from './accounts.js';
export async function streamAgent(projectId, sessionId, text, folder, onEvent, 
/**
 * Optional hidden hint from a + menu item. Sent SEPARATELY from
 * `text` so the persisted message droplet stores only the user's
 * typed text (no hint-bleed in replays). The BFF composes
 * hint+text server-side before sending to the LLM.
 */
hint, 
/**
 * Per-message content-type chip from the composer. Default 'text'
 * is omitted on the wire to keep the common path clean.
 */
contentType, 
/**
 * Client-known previous messageId at submit time. Enables parallel
 * sends in human-DM windows: two POSTs racing to the BFF would
 * otherwise both read the same stale sessionDroplet.lastMessageId
 * and write the same parentMessageId, branching the chain. When
 * the client sends parentMessageId explicitly the server trusts
 * it; the client's local order is authoritative.
 *
 * Null/undefined for the FIRST message in a fresh session OR when
 * the client doesn't track local order; the server falls back to
 * sessionDroplet.lastMessageId.
 */
parentMessageId, 
/**
 * Caller-supplied abort signal. When the caller aborts (Stop
 * button click in an LLM session), the fetch is cancelled and
 * the SSE reader unwinds. The thrown AbortError is caught in
 * the chatStore caller and treated as a clean termination, not
 * an error toast.
 */
signal) {
    const url = `/api/p/${projectId}/chat/sessions/${sessionId}/messages`;
    const headers = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
    };
    // Layer the active account's Bearer token if any (account-switcher
    // mode); cookie auth still works when no token is in localStorage.
    const token = getActiveToken();
    if (token)
        headers.Authorization = `Bearer ${token}`;
    const resp = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
            content: text,
            ...(hint ? { hint } : {}),
            ...(folder ? { folder } : {}),
            ...(contentType && contentType !== 'text' ? { contentType } : {}),
            ...(parentMessageId ? { parentMessageId } : {}),
        }),
        signal,
    });
    if (!resp.ok) {
        let body = '';
        try {
            body = await resp.text();
        }
        catch { /* ignore */ }
        throw new Error(`agent: ${resp.status} ${body || resp.statusText}`);
    }
    if (!resp.body) {
        throw new Error('agent: response has no body');
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    // SSE frames: blocks separated by \n\n. Each block has lines like
    //   event: <name>
    //   data: <json>
    for (;;) {
        const { value, done } = await reader.read();
        if (done)
            break;
        buf += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buf.indexOf('\n\n')) >= 0) {
            const frame = buf.slice(0, nl);
            buf = buf.slice(nl + 2);
            const ev = parseFrame(frame);
            if (ev)
                onEvent(ev);
        }
    }
}
function parseFrame(frame) {
    let event;
    let data;
    for (const line of frame.split('\n')) {
        if (line.startsWith('event:'))
            event = line.slice(6).trim();
        else if (line.startsWith('data:')) {
            const piece = line.slice(5).trim();
            data = data ? data + piece : piece;
        }
    }
    if (!event || !data)
        return null;
    try {
        const parsed = JSON.parse(data);
        return { type: event, ...parsed };
    }
    catch {
        return null;
    }
}
/**
 * Reduce an AgentEvent into the messages array. The last message is
 * the in-flight assistant placeholder; this function mutates it for
 * tool-call / tool-result / final / assistant-saved / error events.
 *
 * In human-only mode the BFF sends user-saved + done with no
 * assistant-related events, so this reducer simply doesn't fire on
 * those paths -- callers control whether an assistant placeholder
 * was pushed in the first place.
 */
export function applyEvent(ev, setMessages) {
    setMessages((prev) => {
        if (prev.length === 0)
            return prev;
        const next = [...prev];
        const last = next[next.length - 1];
        if (!last || last.role !== 'assistant')
            return prev;
        switch (ev.type) {
            case 'thinking':
                // No-op for now -- we render the assistant placeholder regardless.
                break;
            case 'tool-call': {
                const entry = {
                    iteration: ev.iteration ?? 0,
                    toolName: ev.toolName ?? '?',
                    args: ev.args ?? {},
                    state: 'in-flight',
                };
                next[next.length - 1] = { ...last, thinking: [...last.thinking, entry] };
                break;
            }
            case 'tool-result': {
                const updated = [...last.thinking];
                for (let i = updated.length - 1; i >= 0; i--) {
                    if (updated[i] && updated[i].state === 'in-flight' && updated[i].toolName === ev.toolName) {
                        updated[i] = {
                            ...updated[i],
                            state: ev.ok === false ? 'error' : 'ok',
                            ...(ev.preview !== undefined ? { preview: ev.preview } : {}),
                            ...(ev.durationMs !== undefined ? { durationMs: ev.durationMs } : {}),
                        };
                        break;
                    }
                }
                // Extract structured generated-document refs from the tool
                // result for fdn_document_generate. The server-side
                // STRUCTURED_RESULT_TOOLS allow-list ensures only this tool's
                // result lands here in shape; we still defensively narrow on
                // the client because the wire is dynamic and a future tool
                // might join the list with a different shape.
                let genDocsAccum = last.generatedDocuments;
                if (ev.toolName === 'fdn_document_generate'
                    && ev.ok !== false
                    && ev.result
                    && typeof ev.result === 'object') {
                    const r = ev.result;
                    if (Array.isArray(r.documents)) {
                        const newDocs = [];
                        for (const d of r.documents) {
                            if (d
                                && typeof d === 'object'
                                && typeof d.documentId === 'string'
                                && typeof d.filename === 'string') {
                                const dd = d;
                                newDocs.push({
                                    documentId: String(dd.documentId),
                                    filename: String(dd.filename),
                                    contentType: typeof dd.contentType === 'string'
                                        ? dd.contentType
                                        : 'application/octet-stream',
                                    fileSize: typeof dd.fileSize === 'number' ? dd.fileSize : 0,
                                });
                            }
                        }
                        if (newDocs.length > 0) {
                            genDocsAccum = [...(genDocsAccum ?? []), ...newDocs];
                        }
                    }
                }
                next[next.length - 1] = {
                    ...last,
                    thinking: updated,
                    ...(genDocsAccum ? { generatedDocuments: genDocsAccum } : {}),
                };
                break;
            }
            case 'tool-error': {
                const updated = [...last.thinking];
                for (let i = updated.length - 1; i >= 0; i--) {
                    if (updated[i] && updated[i].state === 'in-flight' && updated[i].toolName === ev.toolName) {
                        updated[i] = {
                            ...updated[i],
                            state: 'error',
                            ...(ev.error !== undefined ? { error: ev.error } : {}),
                        };
                        break;
                    }
                }
                next[next.length - 1] = { ...last, thinking: updated };
                break;
            }
            case 'final':
                next[next.length - 1] = {
                    ...last,
                    content: ev.content ?? last.content,
                    state: 'complete',
                    ...(ev.iterations !== undefined ? { iterations: ev.iterations } : {}),
                    ...(ev.toolCallsTotal !== undefined ? { toolCallsTotal: ev.toolCallsTotal } : {}),
                };
                break;
            case 'assistant-saved':
                // BFF persisted the assistant message; replace local id with
                // the persisted messageId so a future loadHistory dedupes.
                if (ev.messageId) {
                    next[next.length - 1] = { ...last, id: ev.messageId };
                }
                break;
            case 'error':
                next[next.length - 1] = {
                    ...last,
                    state: 'error',
                    error: ev.error ?? 'unknown error',
                };
                break;
            case 'side-effect':
            case 'session-title':
            case 'user-saved':
            case 'done':
                // Reducer doesn't touch messages on these; callers handle
                // them at the dispatcher level.
                break;
        }
        return next;
    });
}
